"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown } from "lucide-react";
import MegaMenuController from "./MegaMenuController";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";
import { HEADER_V2_CONFIG } from "./headerConfig";
import { MenuNode } from "./menuAdapter";

interface NavigationBarV2Props {
  categories: MenuNode[];
  loadingCategories?: boolean;
  isMenuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
}

export default function NavigationBarV2({
  categories,
  loadingCategories = false,
  isMenuOpen,
  onMenuOpenChange,
}: NavigationBarV2Props) {
  const pathname = usePathname();
  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(null);


  const { typography, colors } = HEADER_V2_DESIGN_SYSTEM;

  // Automatically close mega menu on route change
  useEffect(() => {
    setHoveredCategoryId(null);
    onMenuOpenChange(false);
  }, [pathname]);

  // Click outside listener to close mega menu when clicking outside navigation & dropdown
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        navContainerRef.current &&
        !navContainerRef.current.contains(e.target as Node)
      ) {
        setHoveredCategoryId(null);
        onMenuOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen, onMenuOpenChange]);

  // 1. Memoize lookup map from cached categories list
  const lookupMap = useMemo(() => {
    const map = new Map<string, MenuNode>();
    const traverse = (node: MenuNode) => {
      map.set(node.id, node);
      if (node.children) {
        node.children.forEach(traverse);
      }
    };
    categories.forEach(traverse);
    return map;
  }, [categories]);

  // 2. Sync menu open state from parent overlay coordinator
  useEffect(() => {
    if (!isMenuOpen) {
      setHoveredCategoryId(null);
    }
  }, [isMenuOpen]);

  const handleCategoryMouseEnter = (catId: string) => {
    setHoveredCategoryId(catId);
    onMenuOpenChange(true);
  };

  const handleCategoryMouseLeave = () => {
    // Controller close delay will handle closing
  };

  const handleMenuClose = () => {
    // setHoveredCategoryId(null);
    // onMenuOpenChange(false);
  };

  if (loadingCategories || categories.length === 0) {
    return (
      <div className={`hidden md:block w-full ${colors.whiteBg} border-t border-slate-100 h-[48px] z-30`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-full flex items-center gap-6 text-[13px] font-semibold text-slate-400">
          Loading categories...
        </div>
      </div>
    );
  }

  // Split categories: first degree links will show at top based on config limits
  const activeCategories = categories.slice(0, HEADER_V2_CONFIG.MAX_DESKTOP_CATEGORIES);

  return (
    <div
      ref={navContainerRef}
      className={`hidden md:block w-full ${colors.whiteBg} border-t border-slate-100 shadow-xs h-[48px] z-30 ${typography.fontFamilySans}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-full flex items-center justify-between relative">
        <nav className="flex items-center gap-1.5 h-full">
          {activeCategories.map((category, idx) => {

            const hasChildren = category.children && category.children.length > 0;
            const isCurrentHovered = isMenuOpen && hoveredCategoryId === category.id;
            const align = idx >= HEADER_V2_CONFIG.ALIGN_RIGHT_INDEX_THRESHOLD ? "right" : "left";

            return (
              <div
                key={category.id}
                className="relative h-full flex items-center"
                onPointerEnter={() => handleCategoryMouseEnter(category.id)}
                onMouseLeave={handleCategoryMouseLeave}
              >
                {/* Top Level Category Link */}
                <Link
                  href={category.href}
                  className={`flex items-center gap-1 px-4 py-2 border-b-[2px] text-[13px] font-semibold transition-all duration-150 cursor-pointer whitespace-nowrap ${
                    isCurrentHovered
                      ? "text-[var(--brand-accent)] border-[var(--brand-accent)]"
                      : "text-slate-700 border-transparent hover:text-[var(--brand-accent)] hover:border-[var(--brand-accent)]/50"
                  }`}
                >
                  <span className="capitalize">{category.label}</span>
                  {hasChildren && (
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      isCurrentHovered ? "rotate-180 text-[var(--brand-accent)]" : "text-slate-400"
                    }`} />
                  )}
                </Link>

                {/* Subcategory Dropdown Panel (Only mounted if it is hovered and has children) */}
                {isCurrentHovered && hasChildren && (
                  <MegaMenuController
                    isOpen={isCurrentHovered}
                    parentCategory={category}
                    lookupMap={lookupMap}
                    align={align}
                    onClose={handleMenuClose}
                  />
                )}
              </div>
            );
          })}
          {/* Static Deals & Offers Link */}
          <Link
            href="/crazy-deals"
            className="flex items-center gap-1 px-4 py-2 border-b-[2px] border-transparent text-[13px] font-semibold text-slate-700 hover:text-[var(--brand-accent)] hover:border-[var(--brand-accent)]/50 transition-all duration-150 cursor-pointer whitespace-nowrap"
          >
            <span>Deals &amp; Offers</span>
          </Link>

          {/* Static Contact Us Link */}
          <Link
            href="/contact"
            className="flex items-center gap-1 px-4 py-2 border-b-[2px] border-transparent text-[13px] font-semibold text-slate-700 hover:text-[var(--brand-accent)] hover:border-[var(--brand-accent)]/50 transition-all duration-150 cursor-pointer whitespace-nowrap"
          >
            <span>Contact Us</span>
          </Link>
        </nav>


      </div>
    </div>
  );
}
